import React, { useState } from "react";
import {
  FiActivity, FiGitMerge, FiAlertTriangle, FiFileText,
  FiList, FiClock, FiCpu, FiPlus, FiMapPin, FiX, FiSearch,
} from "react-icons/fi";
import { allDevices, deviceDummyData } from "../../Data/DeviceData";

import DvcOverviewPanel       from "./panels/DvcOverviewPanel";
import DvcTablePanel          from "./panels/DvcTablePanel";
import DvcTopologyPanel       from "./panels/DvcTopologyPanel";
import DvcAlertsPanel         from "./panels/DvcAlertsPanel";
import DvcEventsPanel         from "./panels/DvcEventsPanel";
import DvcReportsPanel        from "./panels/DvcReportsPanel";
import DvcDeviceDetailPanel   from "./panels/DvcDeviceDetailPanel";

import "./DeviceList.css";

const TABS = [
  { id: "overview",  label: "Overview",       icon: FiActivity      },
  { id: "devices",   label: "Devices",         icon: FiList          },
  { id: "topology",  label: "Topology",         icon: FiGitMerge      },
  { id: "detail",    label: "Device Detail",    icon: FiCpu           },
  { id: "alerts",    label: "Alerts",           icon: FiAlertTriangle },
  { id: "events",    label: "Events",           icon: FiClock         },
  { id: "reports",   label: "Reports",          icon: FiFileText      },
];

const SOLUTIONS = ["STP", "AQM", "Irrigation", "Water", "Facility", "Fleet", "None"];

// ── Add Device Modal ──────────────────────────────────────────────────────────
const AddDeviceModal = ({ onClose }) => {
  const [form, setForm] = useState({ name: "", type: "Sensor", connectivity: "LoRa", site: "", solution: "None" });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  return (
    <div className="dvc-modal-overlay">
      <div className="dvc-modal">
        <div className="dvc-modal-header">
          <span>Add New Device</span>
          <button className="dvc-close-btn" onClick={onClose}><FiX size={18} /></button>
        </div>
        <div className="dvc-settings-form" style={{ marginTop: 0 }}>
          {[
            { label: "Device Name",   key: "name",         type: "input" },
            { label: "Device Role",   key: "type",         type: "select", options: ["Gateway","Sensor","Controller"] },
            { label: "Connectivity",  key: "connectivity", type: "select", options: ["LoRa","WiFi","Ethernet","Cellular","Bluetooth","Modbus"] },
            { label: "Site / Location", key: "site",       type: "input" },
            { label: "Assign to Solution", key: "solution", type: "select", options: SOLUTIONS },
          ].map(f => (
            <div key={f.key} className="dvc-sf-row">
              <label className="dvc-sf-label">{f.label}</label>
              {f.type === "select" ? (
                <select className="dvc-sf-input" value={form[f.key]} onChange={e => set(f.key, e.target.value)}>
                  {f.options?.map(o => <option key={o}>{o}</option>)}
                </select>
              ) : (
                <input className="dvc-sf-input" type="text" value={form[f.key]} onChange={e => set(f.key, e.target.value)} placeholder={`Enter ${f.label.toLowerCase()}`} />
              )}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", padding: "12px 0 0" }}>
          <button className="dvc-ctrl-btn" style={{ background: "#f1f5f9", color: "#374151" }} onClick={onClose}>Cancel</button>
          <button className="dvc-ctrl-btn dvc-ctrl-green" onClick={onClose}>
            <FiPlus size={13} /> Add Device
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Assign Device to Solution Modal ──────────────────────────────────────────
const AssignDeviceModal = ({ onClose }) => {
  const [search, setSearch]   = useState("");
  const [selDev, setSelDev]   = useState([]);
  const [solution, setSolution] = useState("STP");

  const filtered = allDevices.filter(d => {
    if (!search) return true;
    return d.name.toLowerCase().includes(search.toLowerCase()) || d.id.toLowerCase().includes(search.toLowerCase());
  });

  const toggle = (id) => setSelDev(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div className="dvc-modal-overlay">
      {/* flex-column + fixed height so only the device list scrolls internally */}
      <div className="dvc-modal" style={{ maxWidth: 560, width: "95vw", display: "flex", flexDirection: "column", maxHeight: "80vh", overflow: "hidden" }}>
        <div className="dvc-modal-header" style={{ flexShrink: 0 }}>
          <span>Assign Devices to Solution</span>
          <button className="dvc-close-btn" onClick={onClose}><FiX size={18} /></button>
        </div>

        {/* Solution selector — fixed, never scrolls */}
        <div className="dvc-sf-row" style={{ marginTop: 0, flexShrink: 0 }}>
          <label className="dvc-sf-label">Target Solution</label>
          <select className="dvc-sf-input" value={solution} onChange={e => setSolution(e.target.value)}>
            {SOLUTIONS.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        {/* Device list header — fixed */}
        <div style={{ flexShrink: 0 }}>
          <div className="dvc-section-title" style={{ marginTop: 12 }}>Select Devices ({selDev.length} selected)</div>
          <div className="dvc-search-wrap" style={{ margin: "8px 0" }}>
            <FiSearch size={14} color="#9ca3af" />
            <input className="dvc-search" placeholder="Search devices…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        {/* Device list — grows and scrolls */}
        <div className="dvc-assign-device-list" style={{ flex: 1, minHeight: 0, maxHeight: "none" }}>
          {filtered.map(dv => {
            const isSel = selDev.includes(dv.id);
            return (
              <div key={dv.id} className={`dvc-assign-device-row ${isSel ? "dvc-assign-selected" : ""}`} onClick={() => toggle(dv.id)}>
                <input type="checkbox" checked={isSel} readOnly />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{dv.name}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>{dv.id} · {dv.type} · {dv.connectivity}</div>
                </div>
                <span style={{ fontSize: 11, color: "#6b7280" }}>{dv.assignedSolution}</span>
              </div>
            );
          })}
        </div>

        {/* Footer — fixed at bottom */}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12, flexShrink: 0, paddingTop: 8, borderTop: "1px solid #e5e7eb" }}>
          <button className="dvc-ctrl-btn" style={{ background: "#f1f5f9", color: "#374151" }} onClick={onClose}>Cancel</button>
          <button className="dvc-ctrl-btn dvc-ctrl-green" onClick={onClose} disabled={selDev.length === 0} style={{ opacity: selDev.length === 0 ? 0.5 : 1 }}>
            <FiMapPin size={13} /> Assign {selDev.length > 0 ? `(${selDev.length})` : ""} to {solution}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Dashboard ────────────────────────────────────────────────────────────
const DeviceList = () => {
  const [activeTab, setActiveTab]       = useState("overview");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const d = deviceDummyData;

  const critAlerts = d.alerts.filter(a => a.severity === "Critical" && !a.ack).length;

  const handleSelectDevice = (device) => {
    setSelectedDevice(device);
    setActiveTab("detail");
  };

  return (
    <div className="fm-container">
      {/* Custom Header — no solution selector */}
      <div className="dvc-page-header">
        <div className="dvc-page-header-left">
          <div className="dvc-page-title">Device Management</div>
          <div className="dvc-page-sub">Monitor, configure and manage all IoTMATE connected devices</div>
        </div>
        <div className="dvc-page-header-actions">
          <button className="dvc-ctrl-btn dvc-ctrl-green" onClick={() => setShowAddModal(true)}>
            <FiPlus size={14} /> Add Device
          </button>
          <button className="dvc-ctrl-btn dvc-ctrl-blue" onClick={() => setShowAssignModal(true)}>
            <FiMapPin size={14} /> Assign to Solution
          </button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="fm-tab-bar">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`fm-tab ${activeTab === tab.id ? "fm-tab-active" : ""} ${tab.id === "detail" && selectedDevice ? "dvc-tab-has-device" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon size={15} />
            {tab.label}
            {tab.id === "alerts" && critAlerts > 0 && (
              <span className="fm-tab-badge">{critAlerts}</span>
            )}
            {tab.id === "detail" && selectedDevice && (
              <span className="dvc-det-tab-chip">{selectedDevice.id}</span>
            )}
          </button>
        ))}
      </div>

      {/* Panel */}
      <div className="fm-panel">
        {activeTab === "overview"  && <DvcOverviewPanel  d={d} />}
        {activeTab === "devices"   && <DvcTablePanel     onSelectDevice={handleSelectDevice} selectedDeviceId={selectedDevice?.id} />}
        {activeTab === "topology"  && <DvcTopologyPanel  onSelectDevice={handleSelectDevice} selectedDeviceId={selectedDevice?.id} />}
        {activeTab === "detail"    && <DvcDeviceDetailPanel device={selectedDevice} onSelectDevice={handleSelectDevice} />}
        {activeTab === "alerts"    && <DvcAlertsPanel />}
        {activeTab === "events"    && <DvcEventsPanel />}
        {activeTab === "reports"   && <DvcReportsPanel />}
      </div>

      {/* Modals */}
      {showAddModal    && <AddDeviceModal    onClose={() => setShowAddModal(false)} />}
      {showAssignModal && <AssignDeviceModal onClose={() => setShowAssignModal(false)} />}
    </div>
  );
};

export default DeviceList;
